import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/ui/sonner";
import api, { apiError, fmtTime } from "@/lib/api";
import { Panel } from "@/components/terminal";
import { Button } from "@/components/ui/button";

const PILL = {
  PENDING_REVIEW: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  APPROVED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  ACTIVE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  PENDING_ONBOARDING: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  ISSUED: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  REJECTED: "bg-red-500/10 text-red-400 border-red-500/30",
  REVOKED: "bg-red-500/10 text-red-400 border-red-500/30",
  RETIRED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  CONSUMED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  SUSPENDED: "bg-red-500/10 text-red-400 border-red-500/30",
};

function Pill({ v }) {
  return <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-bold tracking-widest whitespace-nowrap ${PILL[v] || PILL.RETIRED}`}>{(v || "—").replace(/_/g, " ")}</span>;
}

export default function EnterpriseAdmin() {
  const [requests, setRequests] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(null);
  const [lastLink, setLastLink] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [sinvoices, setSinvoices] = useState([]);
  const [mapForm, setMapForm] = useState({ network: "BTC", address: "" });
  const [invForm, setInvForm] = useState({ tenant_id: "", amount_usd: "", description: "" });

  const load = useCallback(() => {
    api.get("/enterprise/admin/access-requests").then((r) => setRequests(r.data.requests)).catch((e) => toast.error(apiError(e)));
    api.get("/enterprise/admin/tenants").then((r) => setTenants(r.data.tenants)).catch(() => {});
    api.get("/enterprise/admin/invitations").then((r) => setInvites(r.data.invitations)).catch(() => {});
    api.get("/enterprise/settlement/admin/mappings").then((r) => setMappings(r.data.mappings)).catch(() => {});
    api.get("/enterprise/settlement/admin/invoices").then((r) => setSinvoices(r.data.invoices)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = (id, decision) => {
    const reason = decision === "REJECT" ? window.prompt("Rejection reason (recorded in audit chain):") : null;
    if (decision === "REJECT" && reason === null) return;
    setBusy(`${decision}-${id}`);
    api.post(`/enterprise/access-requests/${id}/review`, { decision, reason: reason || undefined })
      .then((r) => {
        if (decision === "APPROVE" && r.data.invite_path) {
          setLastLink({ id, link: `${window.location.origin}${r.data.invite_path}`, email: r.data });
          toast.success("Approved — tenant created, private invitation issued");
        } else toast.success("Request rejected — audit-chained");
        load();
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const revoke = (id) => {
    setBusy(`rev-${id}`);
    api.post(`/enterprise/invitations/${id}/revoke`, {})
      .then(() => { toast.success("Invitation revoked"); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const retire = (id, org) => {
    if (!window.confirm(`RETIRE TENANT '${org}' (${id})? All records and audit history are preserved; access ends.`)) return;
    setBusy(`ret-${id}`);
    api.post(`/enterprise/admin/tenants/${id}/retire`, { confirm: true })
      .then(() => { toast.success("Tenant retired — records preserved"); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const mapAction = (id, action, body = {}) => {
    setBusy(`map-${action}-${id}`);
    api.post(`/enterprise/settlement/mappings/${id}/${action}`, body)
      .then(() => { toast.success(`Mapping ${action} — audit-chained`); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const proposeMapping = () => {
    setBusy("map-propose");
    api.post("/enterprise/settlement/mappings", mapForm)
      .then(() => { toast.success("Mapping PROPOSED — independent approval required"); setMapForm({ network: "BTC", address: "" }); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const issueInvoice = () => {
    setBusy("inv-issue");
    api.post("/enterprise/settlement/invoices", { ...invForm, amount_usd: Number(invForm.amount_usd) })
      .then(() => { toast.success("Invoice issued — audit-chained"); setInvForm({ tenant_id: "", amount_usd: "", description: "" }); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const voidInvoice = (id) => {
    const reason = window.prompt("Void reason (recorded in audit chain):");
    if (reason === null) return;
    setBusy(`inv-void-${id}`);
    api.post(`/enterprise/settlement/invoices/${id}/void`, { confirm: true, reason })
      .then(() => { toast.success("Invoice voided"); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  return (
    <div className="space-y-6" data-testid="enterprise-admin-page">
      <div>
        <h1 className="text-2xl font-bold font-display" data-testid="enterprise-admin-title">Enterprise Console</h1>
        <p className="text-xs text-slate-500 font-mono mt-1">
          Request Access → Internal Review → Approval → Private Invitation → Enterprise Onboarding. Access is never self-issued.
        </p>
      </div>

      {lastLink && (
        <Panel title="Private Invitation — deliver out-of-band (shown once)" testId="admin-invite-link-panel">
          <div className="flex items-center gap-2">
            <code className="text-xs text-[#D4AF37] font-mono break-all" data-testid="admin-invite-link">{lastLink.link}</code>
            <Button size="sm" variant="outline" data-testid="admin-invite-copy"
                    onClick={() => { navigator.clipboard?.writeText(lastLink.link); toast.success("Copied"); }}
                    className="h-6 text-[10px] font-mono border-[#2D323E] shrink-0">COPY</Button>
            <Button size="sm" variant="outline" data-testid="admin-invite-dismiss" onClick={() => setLastLink(null)}
                    className="h-6 text-[10px] font-mono border-[#2D323E] shrink-0">DISMISS</Button>
          </div>
          <div className="text-[10px] font-mono text-slate-500 mt-2">One-time · 72h · bound to the approved email · revocable below.</div>
        </Panel>
      )}

      <Panel title={`Access Requests (${requests.filter((r) => r.status === "PENDING_REVIEW").length} pending)`} testId="admin-requests-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">Request</th><th className="py-2 px-2">Organization</th>
              <th className="py-2 px-2">Contact</th><th className="py-2 px-2">Submitted</th>
              <th className="py-2 px-2">Status</th><th className="py-2 px-2"></th></tr></thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.request_id} className="border-b border-[#1E2128]" data-testid={`admin-request-${r.request_id}`}>
                  <td className="py-2 px-2 font-mono text-[#D4AF37]">{r.request_id}</td>
                  <td className="py-2 px-2 font-mono text-slate-200">{r.org_name}</td>
                  <td className="py-2 px-2 font-mono text-slate-400">{r.contact_name} · {r.contact_email}</td>
                  <td className="py-2 px-2 font-mono text-slate-500">{fmtTime(r.submitted_at)}</td>
                  <td className="py-2 px-2"><Pill v={r.status} /></td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    {r.status === "PENDING_REVIEW" && (
                      <>
                        <Button size="sm" variant="outline" data-testid={`approve-${r.request_id}`}
                                disabled={busy} onClick={() => review(r.request_id, "APPROVE")}
                                className="h-6 text-[10px] font-mono border-emerald-900/60 text-emerald-400 mr-1">APPROVE</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-${r.request_id}`}
                                disabled={busy} onClick={() => review(r.request_id, "REJECT")}
                                className="h-6 text-[10px] font-mono border-red-900/60 text-red-400">REJECT</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="admin-requests-empty">No access requests.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title={`Tenants (${tenants.length})`} testId="admin-tenants-panel">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">Tenant</th><th className="py-2 px-2">Users</th>
              <th className="py-2 px-2">Status</th><th className="py-2 px-2"></th></tr></thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.tenant_id} className="border-b border-[#1E2128]" data-testid={`admin-tenant-${t.tenant_id}`}>
                  <td className="py-2 px-2 font-mono text-slate-200">{t.org_name}
                    <span className="block text-[9px] text-slate-500">{t.tenant_id}</span></td>
                  <td className="py-2 px-2 font-mono text-slate-400">{t.user_count}</td>
                  <td className="py-2 px-2"><Pill v={t.status} /></td>
                  <td className="py-2 px-2 text-right">
                    {t.status !== "RETIRED" && (
                      <Button size="sm" variant="outline" data-testid={`retire-${t.tenant_id}`}
                              disabled={busy} onClick={() => retire(t.tenant_id, t.org_name)}
                              className="h-6 text-[10px] font-mono border-red-900/60 text-red-400">RETIRE</Button>
                    )}
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="admin-tenants-empty">No tenants.</td></tr>
              )}
            </tbody>
          </table>
        </Panel>

        <Panel title={`Invitations (${invites.length})`} testId="admin-invitations-panel">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">Email</th><th className="py-2 px-2">Role</th>
              <th className="py-2 px-2">Expires</th><th className="py-2 px-2">Status</th><th className="py-2 px-2"></th></tr></thead>
            <tbody>
              {invites.map((i) => (
                <tr key={i.invitation_id} className="border-b border-[#1E2128]" data-testid={`admin-invite-${i.invitation_id}`}>
                  <td className="py-2 px-2 font-mono text-slate-200">{i.email}
                    <span className="block text-[9px] text-slate-500">{i.org_name}</span></td>
                  <td className="py-2 px-2 font-mono text-slate-400">{i.role?.replace(/_/g, " ")}</td>
                  <td className="py-2 px-2 font-mono text-slate-500">{fmtTime(i.expires_at)}</td>
                  <td className="py-2 px-2"><Pill v={i.consumed_at ? "CONSUMED" : i.revoked_at ? "REVOKED" : "ISSUED"} /></td>
                  <td className="py-2 px-2 text-right">
                    {!i.consumed_at && !i.revoked_at && (
                      <Button size="sm" variant="outline" data-testid={`revoke-${i.invitation_id}`}
                              disabled={busy} onClick={() => revoke(i.invitation_id)}
                              className="h-6 text-[10px] font-mono border-red-900/60 text-red-400">REVOKE</Button>
                    )}
                  </td>
                </tr>
              ))}
              {invites.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="admin-invites-empty">No invitations issued.</td></tr>
              )}
            </tbody>
          </table>
        </Panel>
      </div>

      <Panel title="Settlement — Address Mappings (dual control: propose → independent approve → verify → activate)" testId="settlement-mappings-panel">
        <div className="flex gap-2 mb-3">
          <select data-testid="map-network" value={mapForm.network} onChange={(e) => setMapForm({ ...mapForm, network: e.target.value })}
                  className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1.5 text-xs text-slate-200 font-mono">
            <option value="BTC">BTC</option><option value="XRP">XRP</option>
          </select>
          <input data-testid="map-address" value={mapForm.address} onChange={(e) => setMapForm({ ...mapForm, address: e.target.value.trim() })}
                 placeholder="Settlement address" className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1.5 text-xs text-slate-200 font-mono flex-1" />
          <Button data-testid="map-propose-btn" disabled={busy === "map-propose" || !mapForm.address} onClick={proposeMapping}
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs">PROPOSE</Button>
        </div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
            <th className="py-2 px-2">Network</th><th className="py-2 px-2">Address</th><th className="py-2 px-2">Status</th>
            <th className="py-2 px-2">Verification</th><th className="py-2 px-2"></th></tr></thead>
          <tbody>
            {mappings.map((m) => (
              <tr key={m.mapping_id} className="border-b border-[#1E2128]" data-testid={`mapping-${m.mapping_id}`}>
                <td className="py-2 px-2 font-mono text-slate-200">{m.network}</td>
                <td className="py-2 px-2 font-mono text-slate-400 text-[10px]">{m.address}</td>
                <td className="py-2 px-2"><Pill v={m.status} /></td>
                <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{m.verification?.method || "—"}</td>
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  {m.status === "PROPOSED" && (
                    <Button size="sm" variant="outline" data-testid={`map-approve-${m.mapping_id}`} disabled={busy}
                            onClick={() => mapAction(m.mapping_id, "approve")}
                            className="h-6 text-[10px] font-mono border-emerald-900/60 text-emerald-400 mr-1">APPROVE</Button>
                  )}
                  {m.status === "APPROVED" && (
                    <Button size="sm" variant="outline" data-testid={`map-verify-${m.mapping_id}`} disabled={busy}
                            onClick={() => mapAction(m.mapping_id, "verify")}
                            className="h-6 text-[10px] font-mono border-[#2D323E] mr-1">VERIFY</Button>
                  )}
                  {m.status === "VERIFIED" && (
                    <Button size="sm" variant="outline" data-testid={`map-activate-${m.mapping_id}`} disabled={busy}
                            onClick={() => mapAction(m.mapping_id, "activate")}
                            className="h-6 text-[10px] font-mono border-emerald-900/60 text-emerald-400 mr-1">ACTIVATE</Button>
                  )}
                  {m.status !== "RETIRED" && (
                    <Button size="sm" variant="outline" data-testid={`map-retire-${m.mapping_id}`} disabled={busy}
                            onClick={() => mapAction(m.mapping_id, "retire", { confirm: true })}
                            className="h-6 text-[10px] font-mono border-red-900/60 text-red-400">RETIRE</Button>
                  )}
                </td>
              </tr>
            ))}
            {mappings.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-slate-600 font-mono text-xs" data-testid="mappings-empty">No settlement address mappings. No address becomes active from a single action.</td></tr>
            )}
          </tbody>
        </table>
      </Panel>

      <Panel title="Invoices (Hussar Holdings issued — settlement observed on-chain, never custodied)" testId="settlement-invoices-panel">
        <div className="grid grid-cols-4 gap-2 mb-3">
          <select data-testid="inv-tenant" value={invForm.tenant_id} onChange={(e) => setInvForm({ ...invForm, tenant_id: e.target.value })}
                  className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1.5 text-xs text-slate-200 font-mono">
            <option value="">Tenant…</option>
            {tenants.filter((t) => t.status === "ACTIVE").map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.org_name}</option>)}
          </select>
          <input data-testid="inv-amount" value={invForm.amount_usd} onChange={(e) => setInvForm({ ...invForm, amount_usd: e.target.value })}
                 placeholder="Amount USD" className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1.5 text-xs text-slate-200 font-mono" />
          <input data-testid="inv-description" value={invForm.description} onChange={(e) => setInvForm({ ...invForm, description: e.target.value })}
                 placeholder="Description" className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1.5 text-xs text-slate-200 font-mono" />
          <Button data-testid="inv-issue-btn" disabled={busy === "inv-issue" || !invForm.tenant_id || !invForm.amount_usd} onClick={issueInvoice}
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs">ISSUE INVOICE</Button>
        </div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
            <th className="py-2 px-2">Invoice</th><th className="py-2 px-2">Tenant</th><th className="py-2 px-2 text-right">USD</th>
            <th className="py-2 px-2">Asset</th><th className="py-2 px-2 text-right">Expected</th>
            <th className="py-2 px-2 text-right">Paid</th><th className="py-2 px-2">Status</th><th className="py-2 px-2"></th></tr></thead>
          <tbody>
            {sinvoices.map((i) => (
              <tr key={i.invoice_id} className="border-b border-[#1E2128]" data-testid={`sinv-${i.invoice_id}`}>
                <td className="py-2 px-2 font-mono text-[#D4AF37]">{i.invoice_id}</td>
                <td className="py-2 px-2 font-mono text-slate-400">{i.org_name}</td>
                <td className="py-2 px-2 font-mono text-slate-200 text-right">${i.amount_usd}</td>
                <td className="py-2 px-2 font-mono text-slate-400">{i.asset || "—"}</td>
                <td className="py-2 px-2 font-mono text-slate-200 text-right">{i.expected_crypto ?? "—"}</td>
                <td className="py-2 px-2 font-mono text-slate-200 text-right">{i.paid_total ?? 0}</td>
                <td className="py-2 px-2"><Pill v={i.status} /></td>
                <td className="py-2 px-2 text-right">
                  {["ISSUED", "AWAITING_PAYMENT"].includes(i.status) && (
                    <Button size="sm" variant="outline" data-testid={`sinv-void-${i.invoice_id}`} disabled={busy}
                            onClick={() => voidInvoice(i.invoice_id)}
                            className="h-6 text-[10px] font-mono border-red-900/60 text-red-400">VOID</Button>
                  )}
                </td>
              </tr>
            ))}
            {sinvoices.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-slate-600 font-mono text-xs" data-testid="sinvoices-empty">No invoices issued.</td></tr>
            )}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
